import {
    AfterViewInit,
    Component,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {
    BehaviorSubject, combineLatest,
    combineLatestWith,
    concatMap,
    defer, delay,
    finalize,
    forkJoin,
    from,
    Observable,
    of
} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, take, tap, withLatestFrom} from 'rxjs/operators';
import * as R from 'ramda';
import * as RA from 'ramda-adjunct';
import {ITreeState, KEYS, TreeModel, TreeNode} from '@ali-hm/angular-tree-component';
import {Store} from '@ngrx/store';
import {ItemDetailTreeComponent} from './item-detail-tree/item-detail-tree.component';
import {MenuKeyHeaderComponent} from './menu-key-header/menu-key-header.component';
import {Submenu} from '@emm/core/models/submenu.model';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {
    PublishMenusConfirmationDialogComponent
} from '@emm/shared/dialogs/publish-menus-confirmation-dialog/publish-menus-confirmation-dialog.component';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {DialogService} from '@emm/shared/services/dialog.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {MenuTreeFacadeService} from '@emm/menu/services/menu-tree-facade.service';
import {MenuTreeFacadeBasicService} from '@emm/menu/services/menu-tree-facade-basic.service';
import {
    AddItemToMenuDialogComponent
} from '@emm/shared/dialogs/add-item-to-menu-dialog/add-item-to-menu-dialog.component';
import {AddSubmenuDialogComponent} from '@emm/shared/dialogs/add-submenu-dialog/add-submenu-dialog.component';
import {SubmenusActions} from '@emm/menu/store/submenus/submenus.actions';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {
    PasteChildItemDialogComponent
} from '@emm/shared/dialogs/paste-child-item-dialog/paste-child-item-dialog.component';
import {
    DeleteChildItemDialogComponent
} from '@emm/shared/dialogs/delete-child-item-dialog/delete-child-item-dialog.component';
import {
    UpdateChangeSetDialogComponent
} from '@emm/shared/dialogs/update-change-set-dialog/update-change-set-dialog.component';
import {MenuDateUtil} from '@emm/core/util/menu-date.util';
import {FeatureScope} from '@emm/core/util/feature-scope.constants';
import {UserService} from '@emm/core/services/user.service';
import {MenuApiService} from '@emm/menu/services/menu-api.service';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {transformDateService} from '@emm/shared/pipes/formatTimezone.pipe';
import {GenericItemsTreeComponent} from '@emm/management/components/generic-items-tree/generic-items-tree.component';
import {CdkDragDrop, moveItemInArray} from '@angular/cdk/drag-drop';
import {MenuHeaderGlobalComponent} from '@emm/menu/components/menu-header-global/menu-header-global.component';
import {FlagKeys, LaunchDarklyService} from '@emm/core/services/launch-darkly.service';
import {ContextMenuAction} from '@emm/core/models/context-menu-action.model';
import {Clipboard} from '@angular/cdk/clipboard';
import {SharedFacadeService} from '@emm/shared/services/shared-facade.service';

const ITEM_WIDTH_600 = '600px';
const ITEM_WIDTH_1200 = '1200px';
const ITEM_WIDTH_1800 = '1800px';
const ITEM_HEIGHT_800 = '800px';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'emm-menu-tree',
    styleUrls: ['./menu-tree.component.scss'],
    templateUrl: './menu-tree.component.html'
})
export class MenuTreeComponent implements OnInit, AfterViewInit, OnChanges {

    @Input() centerPaneWidth: string;
    @Input() submenuRenamed: BehaviorSubject<boolean>;

    @Output() selectItem = new EventEmitter<{
        prevNode: TreeNode;
        currentNode: TreeNode,
        isSearchMode: boolean,
        isReadOnly: boolean,
    }>();

    @ViewChild(ItemDetailTreeComponent) menuTree: ItemDetailTreeComponent;
    @ViewChild(GenericItemsTreeComponent) searchTree: GenericItemsTreeComponent;
    @ViewChild(MenuKeyHeaderComponent) menuKeyHeader: MenuKeyHeaderComponent;
    @ViewChild(MenuHeaderGlobalComponent) globalHeader: MenuHeaderGlobalComponent;

    anyLoading: boolean;
    anyLoading$ = this.treeFacade.anyLoading$;
    copiedItem: TreeNode;
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    effectiveDate$ = this.treeFacade.effectiveDate$;
    expandAll: boolean;
    expandChecked: boolean;
    isDragAndDropEnabled$ = new BehaviorSubject<boolean>(false);
    isEmpty$ = this.treeFacade.isEmpty$;
    isFutureDate$ = this.treeFacade.isFutureDate$;
    isLoading$ = this.treeFacade.isLoading$;
    isSearchMode$ = new BehaviorSubject<boolean>(false);
    isSearching = of(false);
    searchedTag$ = new BehaviorSubject<string>(null);
    searchFailed = new BehaviorSubject<boolean>(false);
    isAutoExpanding = new BehaviorSubject<boolean>(false);

    panelOpening$ = new BehaviorSubject(false);
    isLoadingSubmenuItems$ = this.treeFacade.isLoadingSubmenuItems$.pipe(
        combineLatestWith(this.panelOpening$),
        map((sources: boolean[]) => R.any(R.equals(true), sources)));

    items$ = this.treeFacade.itemsAsNodes$;
    filteredMenuTree$: Observable<any>;
    menuKey$ = this.treeFacade.menuKeyContext$;
    scope: string;
    treeState: ITreeState;
    fields = [{
        key: 'name',
        type: 'input',
        templateOptions: {
            label: 'Name',
            placeholder: 'Search By Name',
        }},
        {
            key: 'id',
            type: 'input',
            templateOptions: {
                label: 'Tag',
                placeholder: 'Search By Tag',
            }}];

    options = {
        actionMapping: {
            mouse: {
                click: (tree: TreeModel, node: TreeNode) => {
                    if (node?.data?.data?.isSearchResult) {
                        this.isReadOnly$.pipe(take(1)).subscribe(isReadOnly => {
                            this.selectItem.emit({
                                prevNode: this.prevSelectedNode,
                                currentNode: node,
                                isSearchMode: true,
                                isReadOnly,
                            });
                            this.prevSelectedNode = node;
                        });
                    }
                    else if (node?.data?.data) {
                        this.isReadOnly$.pipe(take(1)).subscribe(isReadOnly => {
                            this.selectItem.emit({
                                prevNode: this.prevSelectedNode,
                                currentNode: node,
                                isSearchMode: false,
                                isReadOnly,
                            });
                            this.prevSelectedNode = node;
                        });
                    }
                },
            },
            keys: {
                [KEYS.ENTER]: (tree, node) => alert(`This is ${node.data.name}`)
            },
        },
        allowDrag: (node) => {
            return this.isSubmenu(node) ? false :
                this.isDragAndDropEnabled$.getValue() && !this.anyLoading;
        },
        allowDrop: (node) => {
            return this.isSubmenu(node) ? false :
                this.isDragAndDropEnabled$.getValue() && !this.anyLoading;
        },
        getChildren: (node) => {
            if (node?.children?.length > 0 && node?.data?.data?.isSearchResult) {
                return this.mapFilteredMenuItemToNode(node);
            } else if (node?.hasChildren && node?.data?.data?.isSearchResult) {
                if (this.isAutoExpanding.value) {
                    node.collapse();
                } else {
                    const validPath = node?.id && RA.isNonEmptyString(node.id);
                    return validPath ? this.treeFacade.getChildNodes(node.id) : Promise.resolve();
                }
            } else {
                const validPath = node?.data?.data?.tagPath && RA.isNonEmptyString(node.data.data.tagPath);
                return validPath ? this.treeFacade.getChildNodes(node.data.data.tagPath) : Promise.resolve();
            }
        },
    };
    private prevSelectedNode: TreeNode = null;

    // selected menu filters
    selectedFeatureScope$ = this.treeFacade.selectedFeatureScope$;
    selectedConsumer$ = this.treeFacade.selectedConsumer$;
    selectedLocationSetType$ = this.treeFacade.selectedLocationSetType$;
    selectedLocationSet$ = this.treeFacade.selectedLocationSet$;
    selectedVenue$ = this.treeFacade.selectedVenue$;
    selectedSubmenu$ = this.treeFacade.selectedSubmenu$;
    selectedSearchSubmenu$ = this.treeFacade.selectedSearchSubmenu$;

    // menu tree based on given feature scope
    nodes$ = this.treeFacade.submenusAsGenericItems$;

    // consumer menu assignment form
    showCheckedSubmenusOnly: boolean;
    selectViewNodes: Submenu[] = [];
    sortedPreviewNodes: Submenu[] = [];
    showNextButton = true;
    showSaveButton = false;
    panelExpanded = false;
    submenus;
    selectedSubmenusPaste = [];
    selectedSubmenusDelete = [];
    isCopied: boolean;

    isSubmenuCacheEnabled: boolean;
    forceOverrides = false;
    canPublish = this.treeFacade.canPublish$;
    isReadOnly$ = combineLatest([
        this.authFacade.isReadOnly$
    ]).pipe(
        map(([authReadOnly]) => authReadOnly));
    isSuperAdmin = this.userService.hasSuperAdminRole();
    isAdmin = this.userService.hasAdminRole();
    menuLastPublishedString = new BehaviorSubject<string>(null);
    newDisableToggle$ = this.launchDarklyService.getBooleanFlag(FlagKeys.APPLY_LEAST_PRECEDENCE_FOR_ITEM_LOCKS);
    isNewDisableFlow$ = new BehaviorSubject<boolean>(false);
    consumersAvailableForMenuDownload = [
        'MOBILE',
        'MEMBERSHIP_REWARDS',
        'ONLINE_ORDERING_DELIVERY',
        'ONLINE_ORDERING_PICKUP',
        'SAUCES_DRESSINGS',
        'SPOTLIGHT',
        'FOOD_TRUCK',
        'MASTER_REWARDS_MENU',
        'COMMERCEPOINT',
        'COMMERCEPOINT_CATERING',
        'DCB',
    ];

    searchTreeContextMenuActions = [
        {
            click: (node) => this.copyTagpath(node),
            enabled: R.T,
            icon: R.always('file_copy'),
            text: R.always('Copy Tagpath'),
            visible: () => true,
        },
    ] as ContextMenuAction<TreeNode>[];

    constructor(
        private authFacade: AuthFacadeService,
        private dialog: MatDialog,
        private dialogService: DialogService,
        private notificationService: NotificationService,
        private treeFacade: MenuTreeFacadeService,
        private treeFacadeBasic: MenuTreeFacadeBasicService,
        private store: Store,
        private userService: UserService,
        private api: MenuApiService,
        private launchDarklyService: LaunchDarklyService,
        private clipboard: Clipboard,
        private sharedFacadeService: SharedFacadeService,
    ) {}

    ngOnInit(): void {
        this.selectedLocationSetType$.subscribe();
        combineLatest([this.selectedConsumer$, this.effectiveDate$])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter(([consumer, effectiveDate]) => {
                    const today = new Date();
                    const compareDate = new Date(effectiveDate);
                    const isToday = compareDate.getFullYear() === today.getFullYear() &&
                        compareDate.getMonth() === today.getMonth() &&
                        compareDate.getDate() === today.getDate();
                    return consumer && isToday;
                }),
            ).subscribe(([consumer, isToday]) => {
                if (this.consumersAvailableForMenuDownload.includes(consumer?.tag) && isToday) {
                    this.getLastPublishedDateForMenu();
                }
            }
        );
        combineLatest([this.selectedConsumer$, this.effectiveDate$, this.selectedLocationSetType$,
            this.selectedLocationSet$])
            .pipe(
                takeUntilDestroyed(this.destroyRef),
            ).subscribe(([consumer, effectiveDate, locationSetType, locationSet]) => {
                if (this.isSearchMode$.value && locationSet) {
                    if (this.globalHeader?.menuSearch) {
                        this.globalHeader.menuSearch.searchFilter = '';
                        this.searchTree.items = null;
                        this.exitSearchMode();
                    }
                    if (this.menuKeyHeader?.menuSearch) {
                        this.menuKeyHeader.menuSearch.searchFilter = '';
                        this.searchTree.items = null;
                        this.exitSearchMode();
                    }
                }
            }
        );
        this.canPublish.next(this.isSuperAdmin || this.isAdmin);
        this.treeFacade.submenus$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => this.submenus = result);
        this.anyLoading$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe((isLoading) => {
                this.anyLoading = isLoading;
            });
        this.submenuRenamed.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

        this.treeFacade.updateMenuItem$
            .pipe(
                filter(RA.isTrue),
                withLatestFrom(this.treeFacade.selectedItemDetail$),
                map(([_, selectedItem]) => selectedItem),
                filter(RA.isNotNil),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((selectedItem) => {
                const node: TreeNode = this.menuTree?.menuTree?.treeModel?.getNodeBy(
                    (item: TreeNode) => item.data.data.tag === selectedItem.tag
                );
                if (node?.parent && node?.parent?.hasChildren) {
                    node?.parent?.loadNodeChildren().then(() => this.submenuRenamed.value === true ?
                        this.treeFacade.loadSubmenus() : null);
                }
            });

        this.initClientForm();
        this.selectedFeatureScope$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            this.scope = result;
            this.treeFacade.changeConsumerContext();
        });
        this.getSubmenuCacheEnabled();
        this.newDisableToggle$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value =>
            this.isNewDisableFlow$.next(value));
    }

    ngAfterViewInit(): void {
        if (this.menuTree) {
            this.treeFacade.refresh$.pipe(
                this.menuTree.reloadItemsAndSetActive(),
                takeUntilDestroyed(this.destroyRef)
            );
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        this.treeFacade.selectedSubmenusPaste$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            for (const submenu of result) {
                if (this.selectedSubmenusPaste.includes(submenu)) {
                    this.selectedSubmenusPaste.splice(this.selectedSubmenusPaste.indexOf(submenu));
                } else {
                    this.selectedSubmenusPaste.push(submenu);
                }
            }
        });
        this.treeFacade.selectedSubmenusDelete$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(result => {
            for (const submenu of result) {
                if (this.selectedSubmenusDelete.includes(submenu)) {
                    this.selectedSubmenusDelete.splice(this.selectedSubmenusDelete.indexOf(submenu));
                } else {
                    this.selectedSubmenusDelete.push(submenu);
                }
            }
        });
        if (changes) {
            this.canPublish.next(this.isSuperAdmin || this.isAdmin);
        }
    }

    addItem(node: TreeNode, itemTypeFilter: string = null): void {
        const currentSubmenuItems = node.data.children ? node.data.children : [];
        const existingItemTags = [];
        const newTags = [];
        let hasDuplicateTag = false;
        let duplicateTag;

        if (currentSubmenuItems.length > 0) {
            currentSubmenuItems.forEach(item => existingItemTags.push(item.data.tag));
        }

        const dialogRef = this.dialog.open(AddItemToMenuDialogComponent, {
            data: {
                displayAddGlobal: false,
                itemTypeFilter
            },
            width: ITEM_WIDTH_600
        });
        dialogRef
            .afterClosed()
            .pipe(
                filter(RA.isNotNil),
                switchMap((newItem) => {
                    newItem.value.forEach(item => newTags.push(item.tag));
                    newTags.forEach(tag => {
                        if (existingItemTags.includes(tag)) {
                            hasDuplicateTag = true;
                            duplicateTag = tag;
                        }
                    });
                    if (node && !hasDuplicateTag) {
                        return this.treeFacade
                            .addItem(node.data.data, newItem.value)
                            .pipe(switchMap(() => defer(() => from(node.loadNodeChildren())).pipe(map(() => true))));
                    } else {
                        this.notificationService.error(`Error: An item with tag, ${duplicateTag}, already exists.`);
                        return of(false)
                    }
                }),
                filter(RA.isTrue),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificationService.success('Added Successfully');
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    // create submenu for global menu builder
    createGlobalSubMenu(): void {
        const itemTypeFilter = 'SUBMENU';
        const dialogRef = this.dialog.open(AddSubmenuDialogComponent, {
            data: {
                displayAddGlobal: false,
                itemTypeFilter,
                title: 'Add Global Submenu',
                scope: this.scope,
                submenus: this.submenus
            },
            width: ITEM_WIDTH_600
        });
        dialogRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(
                result => {
                    this.store.dispatch(SubmenusActions.add({ submenu: result?.newSubmenu }));
                }
            );

    }

    sortByName() {
        this.treeFacade.submenusAsGenericItems$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    sortByTag() {
        this.treeFacade.submenusSortedByTag$
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    // create submenu for legacy
    public createSubMenu(): void {
        this.addItem(null, 'SUBMENU');
    }

    public toggleItemDisabled(node: TreeNode, path = node.data.data.tagPath): void {
        const item = node.data.data;
        const parentNode = node.parent;
        const currentlyDisabled = item.disabled;
        let action;
        if (this.isNewDisableFlow$.value) {
             action = !currentlyDisabled || item?.disabledCode !== 111 ? 'disable' : 'enable';
        } else {
            action = currentlyDisabled ? 'enable' : 'disable';
        }
        const confirmMessage = `Are you sure you want to ${action} ${item.name}?`;

        this.dialogService
            .openConfirmDialog(confirmMessage, null)
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                map(() => path),
                switchMap((tagPath) =>
                    defer(() =>
                        this.isNewDisableFlow$.value ?
                        this.treeFacade
                            .toggleItemDisabled(tagPath, { disabled: item.disabledCode !== 111 ? true :
                                    !currentlyDisabled }, this.forceOverrides)
                            .pipe(
                                switchMap((_) =>
                                    parentNode && parentNode?.parent?.data?.data
                                        ? parentNode?.parent.loadNodeChildren().then()
                                        : Promise.resolve({}),
                                )
                            ) : this.treeFacade
                                .toggleItemDisabled(tagPath, { disabled: !currentlyDisabled }, this.forceOverrides)
                                .pipe(
                                    switchMap((_) =>
                                        parentNode && parentNode?.parent?.data?.data
                                            ? parentNode?.parent?.loadNodeChildren().then()
                                            : Promise.resolve({}),
                                    )
                                )
                    )
                ),
                switchMap(() => {
                    if (!node.isRoot) {
                        return defer(() => from(parentNode.loadNodeChildren())).pipe(map(() => true));
                    }
                    return this.treeFacade.reloadItems();
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.notificationService.success(`${item.tag} was successfully ${action}d`);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error.message || error || API_ERRORS.GenericErrorMessage);
                }
            });
    }


    deleteItem(node: TreeNode): void {
        const item: ItemDetail = node.data.data;
        this.dialogService
            .openConfirmDialog('Are you sure you want to delete', item.name, 'and all items under it?')
            .afterClosed()
            .pipe(
                filter(RA.isTruthy),
                withLatestFrom(this.treeFacade.selectedItemDetail$),
                switchMap(([_, selectedItemDetail]) => defer(() =>
                    this.treeFacade.deleteItem(item.tagPath, item.name, selectedItemDetail))),
                switchMap((_) => {
                    if (!node.isRoot) {
                        const parentNode = node.parent;
                        return defer(() => from(parentNode.loadNodeChildren())).pipe(map(() => true));
                    }

                    return this.treeFacade.reloadItems();
                }),
                filter(RA.isTrue),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: () => {
                    this.selectItem.emit(null);
                },
                error: () => {
                    this.notificationService.error(`Error deleting ${node.data.data.name}`);
                }
            });
    }

    pasteItemCascade(node: TreeNode): void {
        this.effectiveDate$.pipe(
            combineLatestWith(this.menuKey$, this.selectedFeatureScope$),
            take(1),
            switchMap(([effectiveDate, menuKey, featureScope]) => {
                const dialogRef = this.dialog.open(PasteChildItemDialogComponent, {
                    data: {
                        featureScope,
                        effectiveDate,
                        menuKey,
                        parent: node,
                        copiedItem: this.copiedItem.data.data.tagPath
                    },
                    width: ITEM_WIDTH_1200
                });
                return dialogRef.afterClosed();
            }),
            map(pasteResult => {
                return {
                    pasteResult,
                    child: pasteResult
                };
            }),
            filter(result => !!result.child && !!node),
            withLatestFrom(this.treeFacade.selectedItemDetail$),
            switchMap(([result, selectedItemDetail]) =>
                defer(() =>
                    this.treeFacade.pasteItemCascade(
                        result.pasteResult.submenuTag,
                        result.pasteResult.parentPath.substring(1),
                        this.copiedItem.data.data.tagPath,
                        selectedItemDetail,
                        result.pasteResult.position
                    )
                )
            ),
            tap((_) => {
                this.notificationService.success('Added Successfully');
            }),
            switchMap(() => defer(() => this.treeFacade.reloadItems())),
            filter(RA.isTrue),
            takeUntilDestroyed(this.destroyRef)
        )
            .subscribe();
    }


    deleteItemCascade(node: TreeNode): void {
        this.effectiveDate$.pipe(
            combineLatestWith(this.menuKey$, this.selectedFeatureScope$),
            take(1),
            switchMap(([effectiveDate, menuKey, featureScope]) => {
                const dialogRef = this.dialog.open(DeleteChildItemDialogComponent, {
                    data: {
                        featureScope,
                        parent: node,
                        menuKey,
                        effectiveDate
                    },
                    height: ITEM_HEIGHT_800 ,
                    width: ITEM_WIDTH_1800
                });

                return dialogRef.afterClosed();
            }),
            takeUntilDestroyed(this.destroyRef)
        ).subscribe();
    }



    copyItem(node: TreeNode): void {
        this.copiedItem = node;
        this.treeFacade.copyItem(this.copiedItem.data.data.tagPath);
        this.showCopiedSnackbar();
    }

    showCopiedSnackbar() {
        const copiedSnackbar = document.getElementById('copiedSnackbar');
        copiedSnackbar.className = 'show';
        setTimeout(() => {
            copiedSnackbar.className = copiedSnackbar.className.replace('show', '');
        }, 5000);
    }

    closeCopiedSnackbar() {
        const copiedSnackbar = document.getElementById('copiedSnackbar');
        copiedSnackbar.className = copiedSnackbar.className.replace('show', '');
    }

    pasteItem(node: TreeNode): void {
        if (this.copiedItem) {
            const fromPath = this.copiedItem.data.data.tagPath;
            const toPath = `${node?.data?.data?.tagPath}`;
            this.treeFacade
                .pasteItem(fromPath, toPath)
                .pipe(
                    tap((_) => (this.copiedItem = undefined)),
                    filter(RA.isTrue),
                    switchMap(() => defer(() => from(node.loadNodeChildren()))),
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe();
        }
    }

    setTreeState($event) {
        this.treeState = $event;
    }

    moveItem({ fromPath, toPath, index, onSuccess, dragNode }): void {
        let expandedIds = [];
        let hadError = false;
        if (this.treeState) {
            expandedIds = Object.keys(this.treeState?.expandedNodeIds);
        }
        this.treeFacade.moveItem(fromPath, toPath, index)
            .pipe(
                concatMap((value: boolean) => {
                     if (value) {
                        return this.handleMoveSuccess(dragNode.data.tag, onSuccess);
                    } else {
                         hadError = true;
                        return this.handleMoveError(expandedIds, dragNode);
                    }
                }),
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    if (hadError) this.expandPreviouslyExpandedNodes(expandedIds);
                })
            )
            .subscribe();
    }

    handleMoveSuccess(itemTag: string, successFunction: () => {}) {
        this.notificationService.success(`${itemTag} was successfully moved`);
        return of(successFunction);
    }

    handleMoveError(expandedIds: string[], dragNode: TreeNode) {
        this.showMoveFailedSnackbar();
        return of(null).pipe(
            concatMap(() => of(this.strikeBadDragNode(dragNode))),
            delay(1000),
            concatMap(() => of(this.collapseExpandedNodes([...expandedIds]))),
            concatMap(() => of(this.resetTreeState(expandedIds))),
        );

    }

    strikeBadDragNode(dragNode: TreeNode) {
        const node = this.menuTree.menuTree.treeModel.getNodeById(dragNode?.id);
        node.data.isStruck = true;
        return node.data.isStruck;
    }

    collapseExpandedNodes(expandedIds: string[]): number {
        expandedIds?.shift();
        expandedIds?.forEach((id) => {
            this.menuTree.menuTree.treeModel.getNodeById(id)?.collapse();
        });
        return expandedIds.length;
    }

    resetTreeState(expandedIds): Observable<any> {
        const observables = expandedIds.map(id => this.menuTree.menuTree.treeModel.getNodeById(id)?.loadNodeChildren());
        return forkJoin(observables);
    }

    expandPreviouslyExpandedNodes(expandedIds) {
        expandedIds.shift();
        expandedIds?.forEach((id) => {
            this.menuTree.menuTree.treeModel.getNodeById(id)?.expand();
        });
        return expandedIds.length;
    }

    renameItem(): void {
        this.submenuRenamed.next(true);
    }

    isSubmenu(node: TreeNode): boolean {
        return node.parent === null || node.parent.parent === null; // submenu > root > null
    }



    viewChangeSets(): void {
        this.treeFacade.viewChangeSets();
    }



    updateChangeSet(): void {
        this.effectiveDate$
            .pipe(
                take(1),
                withLatestFrom(this.selectedFeatureScope$,
                    this.selectedLocationSet$,
                    this.selectedSubmenu$,
                    this.selectedSearchSubmenu$),
                switchMap(([effectiveDate, featureScope, selectedLocationSet, selectedSubmenu, searchSubmenu]) => {
                    const dialogRef = this.dialog.open(UpdateChangeSetDialogComponent, {
                        data: {effectiveDate, featureScope, location: selectedLocationSet.tag,
                                submenu: searchSubmenu ? searchSubmenu : selectedSubmenu},
                        width: ITEM_WIDTH_600,
                    });

                    return dialogRef.afterClosed().pipe(
                        tap((result) => {
                            setTimeout(() => {
                                if (result && result.action === 'updated') {
                                    this.redirectToDate(result.updatedDate, effectiveDate);
                                } else {
                                    this.treeFacade.loadFutureChangeSets();
                                }
                            }, 1000)
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }



    redirectToDate(date: Date, effectiveDate: string): void {
        const sameDay = MenuDateUtil.isEqual(new Date(effectiveDate), new Date(date));
        if (!sameDay) {
            this.treeFacade.redirectToDate(date.toISOString());
        }
    }



    toggleDragAndDrop(checked: boolean): void {
        this.isDragAndDropEnabled$.next(checked);
    }



    // display progress bar as soon as panel animation starts opening
    async handlePanelOpen() {
        this.isDragAndDropEnabled$.next(false);
        await this.panelOpening$.next(true);
    }



    // fetch submenu tree only after panel finished expanding due to slow animations if done while opening
    handlePanelExpand({id}): void {
        this.handlePanelOpen().then(() => {
            this.treeFacade.selectSubmenu(id); // submenu id same as tag
            this.isCopied = !!this.copiedItem;
            this.panelExpanded = true;
            this.panelOpening$.next(false);
        });
    }



    handlePanelClose({id}): void {
        this.treeFacade.deselectSubmenu(id);
        this.panelExpanded = false;
    }



    // consider alternate form binding (instead of form array) when implementing sorting
    initClientForm(): void {
        this.nodes$
            .pipe(
                withLatestFrom(this.selectedFeatureScope$),
                filter(([_, featureScope]) => featureScope === FeatureScope.Client),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(([submenus, featureScope]) => {
                this.selectViewNodes = [];
                this.sortedPreviewNodes = [];

                submenus.forEach(submenu => {
                    this.selectViewNodes.push(submenu);

                    if (submenu.assigned) {
                        this.sortedPreviewNodes.push(submenu);
                    }
                });
            });

        this.showCheckedSubmenusOnly = false;
    }



    // consider alternate form binding (instead of form array) when implementing sorting
    handleCheckboxSubmenu(submenu, event) {
        const svNodeIndex = this.selectViewNodes.findIndex(nd => nd.id === submenu.id);
        const svNode = Object.assign({}, this.selectViewNodes[svNodeIndex]);

        if (event.checked) {

            svNode.assigned = true;
            this.selectViewNodes[svNodeIndex] = svNode;

            this.sortedPreviewNodes.push(svNode);

        } else {

            svNode.assigned = false;
            this.selectViewNodes[svNodeIndex] = svNode;

            this.sortedPreviewNodes = this.sortedPreviewNodes.filter(spn => {
                return spn.id !== submenu.id; });
        }
    }



    // consider alternate form binding (instead of form array) when implementing sorting
    handleSaveAssignments(): void {
        // Convert nodes to api expected objects
        const submitObjects = [];
        this.sortedPreviewNodes.forEach(sn => {
            submitObjects.push({ submenu: sn.id, sort: sn.sortOrder});
        });

        this.treeFacade.saveAssignments(submitObjects);
        this.resetToInitialView();
    }

    resetToInitialView() {
        this.showCheckedSubmenusOnly = false;
        this.showNextButton = true;
        this.showSaveButton = false;
    }

    handleNextButtonClicked(): void {
        this.showNextButton = false;
        this.showCheckedSubmenusOnly = true;
        this.orderNodesBySortOrder();
        this.resetSortOrderValues();
        this.showSaveButton = true;
    }

    isNumberString = (value: string): boolean => {
        return !isNaN(Number(value));
    };

    handlePublishButtonClicked() {
        let consumer = null;
        let locationSetTag = null;
        let locationTag = null;
        let countryCode = null;
        let venueId = null;
        let isCustomLocationSet = false;
        this.selectedLocationSetType$.pipe(take(1)).subscribe((locationSetType) => {
            isCustomLocationSet = locationSetType.tag === 'CUSTOM'});
        this.selectedConsumer$.pipe(take(1)).subscribe((selectedConsumer) =>
            consumer = selectedConsumer.tag);
        this.selectedLocationSet$.pipe(take(1)).subscribe((locationSet) => {
            if (locationSet.tag === 'US' || locationSet.tag === 'CA' || locationSet.tag === 'PR') {
                countryCode = locationSet.tag;
            } else {
                if (!venueId) {
                    this.isNumberString(locationSet.tag) ? locationTag = locationSet.tag : locationSetTag = locationSet.tag;
                }
            }
        });
        this.selectedVenue$.pipe(take(1)).subscribe((selectedVenue) => {
            if (selectedVenue) {
                venueId = selectedVenue.venueId;
            }
        });
        if (!venueId) {
            let storesIncludedEventCount;
            if (locationSetTag !== 'CHAIN' && locationSetTag !== 'LOCATION' && locationSetTag !== 'COUNTRY') {
                storesIncludedEventCount = this.countMenusToPublishWithStoresIncluded(consumer, locationSetTag, locationTag,
                    countryCode);
            }
            this.countMenusToPublish(consumer, locationSetTag, locationTag, countryCode)
                .then((eventCount) => {
                    this.dialog.open(PublishMenusConfirmationDialogComponent, {
                        data: {
                            selectedLocation: locationTag,
                            selectedLocationSet: locationSetTag,
                            countryCode,
                            consumer,
                            eventCount,
                            storesIncludedEventCount,
                            isSuperAdmin: this.isSuperAdmin,
                            isAdmin: this.isAdmin,
                            isCustomLocationSet
                        },
                        backdropClass: 'blurred-background',
                        position: {top: '100px'},
                        width: '470px'
                    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
                        if (result?.publishMenu) {
                            this.publishMenus(consumer, locationSetTag, locationTag, countryCode,
                                result?.includeStores);
                        } else {
                            this.notificationService.error('Publish Cancelled');
                        }
                    });
                });
        } else {
            this.countVenuesToPublish(venueId).then((eventCount) => {
                this.dialog.open(PublishMenusConfirmationDialogComponent, {
                    data: {
                        selectedLocation: locationTag,
                        selectedLocationSet: locationSetTag,
                        countryCode,
                        consumer,
                        eventCount,
                        venueId,
                        isSuperAdmin: this.isSuperAdmin,
                        isAdmin: this.isAdmin,
                    },
                    backdropClass: 'blurred-background',
                    position: {top: '100px'},
                    width: '470px'
                }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
                    if (result.publishVenue) {
                        this.publishVenues(venueId);
                    } else {
                        this.notificationService.error('Publish Cancelled');
                    }
                });
            })
        }
    }

    publishMenus(
        consumer: string,
        locationSetTag?: string, locationTag?: string, countryCode?: string, includeStores?: boolean) {
        if (locationSetTag === 'CHAIN' && (this.isSuperAdmin || this.isAdmin)) {
            this.api.publishChainwide(consumer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
                (response) => {
                    const eventCount = response?.eventCount;
                    this.notificationService.success(`Successfully Published ${eventCount} Menus`);
                },
                (e) => this.notificationService.error(`Publish Failed`)
            );
        }
        else if (locationSetTag  !== null && (this.isSuperAdmin || this.isAdmin)) {
            this.api.publishLocationSet(locationSetTag, consumer, includeStores).pipe(
                takeUntilDestroyed(this.destroyRef)).subscribe(
                (response) => {
                    const eventCount = response?.eventCount;
                    this.notificationService.success(`Successfully Published ${eventCount} Menus`);
                },
                (e) => this.notificationService.error(`Publish Failed`),
            );
        }
        else if (locationTag !== null && (this.isSuperAdmin || this.isAdmin)) {
            this.api.publishByStore(locationTag, consumer).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
                (response) => {
                    const eventCount = response?.eventCount;
                    this.notificationService.success(`Successfully Published ${eventCount} Menus`);
                },
                (e) => {
                    this.notificationService.error(`Publish Failed`);
                }
            );
        }
        else if (countryCode && (this.isSuperAdmin || this.isAdmin)) {
            this.api.publishNational(countryCode, consumer, includeStores).pipe(
                takeUntilDestroyed(this.destroyRef)).subscribe(
                (response) => {
                    const eventCount = response?.eventCount;
                    this.notificationService.success(`Successfully Published ${eventCount} Menus`);
                },
                (e) => this.notificationService.error(`Publish Failed`),
            );
        }
    }

    publishVenues(venueId?: string) {
        if (venueId && (this.isSuperAdmin || this.isAdmin)) {
            this.api.publishVenue(venueId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
                (response) => {
                    const eventCount = response?.eventCount;
                    this.notificationService.success(`Successfully Published ${eventCount} Menus`);
                },
                (e) => this.notificationService.error(`Publish Failed`),
            );
        }
    }

    countVenuesToPublish(venueId?: string): Promise<number>{
        let eventCount = 0;
        return new Promise((resolve, reject) => {
            if (venueId && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishVenueDryRun(venueId).pipe(take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (e) => this.notificationService.error(`Publish Failed`),
                );
            }});
    }

    countMenusToPublish(
        consumer: string,
        locationSetTag?: string, locationTag?: string, countryCode?: string
    ): Promise<number> {
        let eventCount = 0;

        return new Promise((resolve, reject) => {
            if (locationSetTag === 'CHAIN' && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishChainwideDryRun(consumer).pipe(take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else if (locationSetTag !== null && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishLocationSetDryRun(locationSetTag, consumer,false).pipe(
                    take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else if (locationTag !== null && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishByStoreDryRun(locationTag, consumer).pipe(take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else if (countryCode && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishNationalDryRun(countryCode, consumer, false).pipe(
                    take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else {
                reject(new Error('No valid criteria provided for menu count'));
            }
        });
    }

    countMenusToPublishWithStoresIncluded(
        consumer: string,
        locationSetTag?: string, locationTag?: string, countryCode?: string
    ): Promise<number> {
        let eventCount = 0;

        return new Promise((resolve, reject) => {
            if (locationSetTag !== null && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishLocationSetDryRun(locationSetTag, consumer,true).pipe(
                    take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else if (countryCode && (this.isSuperAdmin || this.isAdmin)) {
                this.api.publishNationalDryRun(countryCode, consumer, true).pipe(
                    take(1)).subscribe(
                    (response) => {
                        eventCount = response?.eventCount;
                        resolve(eventCount);
                    },
                    (error) => {
                        this.notificationService.error(`Publishing Dry Run Failed`);
                        reject(error);
                    }
                );
            } else {
                return;
            }
        });
    }

    getFilters() {
        let selectedConsumer = null;
        let selectedLocation = null;
        let selectedLocationType = null;
        let selectedVenue = null;
        let selectedEffectiveDate = null;
        let selectedFeatureScope = null
        this.selectedConsumer$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((consumer) =>
            selectedConsumer = consumer);
        this.selectedLocationSet$.pipe(take(1)).subscribe((location) =>
            selectedLocation = location);
        this.selectedLocationSetType$.pipe(take(1)).subscribe((locationSetType) =>
            selectedLocationType = locationSetType);
        this.selectedVenue$.pipe(take(1)).subscribe((venue) =>
            selectedVenue = venue);
        this.effectiveDate$.pipe(take(1)).subscribe((effectiveDate) =>
            selectedEffectiveDate = new Date(effectiveDate).toISOString().split('T')[0]);
        this.selectedFeatureScope$.pipe(take(1)).subscribe((scope) =>
            selectedFeatureScope = scope);
        return {
            selectedLocation,
            selectedVenue,
            selectedLocationType,
            selectedConsumer,
            selectedEffectiveDate,
            selectedFeatureScope
        };
    }

    handleDownloadMenuClicked(event) {
        const filters = this.getFilters();
        if (filters?.selectedConsumer?.tag === 'MOBILE' && filters?.selectedLocationType?.tag === 'LOCATION' &&
            filters?.selectedLocation?.tag) {
            this.downloadMenu(event, filters);
        } else {
            this.downloadMenu(this.getEndpoint(filters), filters);
        }
    }

    downloadMenu(endpoint: string, filters) {
        this.api.downloadMenu(endpoint)?.pipe(take(1)).subscribe({
            next: (response) => {
                const blob = new Blob([response], {type: 'application/octet-stream'});
                const url = window.URL.createObjectURL(blob);
                const downloadElement = document.createElement('a');
                downloadElement.href = url;
                downloadElement.download = `${filters?.selectedLocation?.tag} - ${filters?.selectedConsumer?.name} Menu.json`;
                downloadElement.click();
                window.URL.revokeObjectURL(url);
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    getLastPublishedEndpoint(filters): string {
        let endpoint;
        if (filters?.selectedVenue && filters?.selectedLocationType?.tag !== 'COUNTRY') {
            return endpoint = `menu/metadata/${filters?.selectedVenue.venueId}/4.0?format=client`
        } else {
            if (filters?.selectedLocation?.tag) {
                switch (filters?.selectedConsumer?.tag) {
                    case 'COMMERCEPOINT':
                        return endpoint = `menu/metadata/commercepoint/${filters?.selectedLocation?.tag}/4.0?format=client`;
                    case 'COMMERCEPOINT_CATERING':
                        return endpoint = `menu/metadata/commercepoint-catering/${filters?.selectedLocation?.tag}/4.0?format=client`;
                    case 'DCB':
                        return endpoint = `menu/internal/raw/metadata/digital-communications-boards/${filters?.selectedLocation?.tag}`;
                    case 'ONLINE_ORDERING_DELIVERY':
                        return endpoint = `menu/metadata/catering/delivery/${filters?.selectedLocation?.tag}/3.1?format=client`;
                    case 'ONLINE_ORDERING_PICKUP':
                        return endpoint = `menu/metadata/catering/delivery/${filters?.selectedLocation?.tag}/3.1?format=client`;
                    case 'SAUCES_DRESSINGS':
                        return endpoint = `menu/metadata/sauces/${filters?.selectedLocation?.tag}/3.1?format=client`;
                    case 'SPOTLIGHT':
                        return endpoint = `menu/metadata/spotlight/${filters?.selectedLocation?.tag}/3.2?format=client`;
                    case 'FOOD_TRUCK':
                        return endpoint = `menu/metadata/food-truck/${filters?.selectedLocation?.tag}/3.1?format=client`;
                    case 'MASTER_REWARDS_MENU':
                        if (filters?.selectedLocationType?.tag === 'COUNTRY') {
                            return `menu/metadata/master-rewards/${filters?.selectedLocation?.tag}/3.2?format=client`;
                        } else {
                            break;
                        }
                    case 'MEMBERSHIP_REWARDS':
                        return `menu/metadata/rewards/${filters?.selectedLocation?.tag}/3.2?format=client`;
                    case 'MOBILE':
                        if (filters?.selectedLocationType?.tag === 'LOCATION') {
                            return endpoint = 'MOBILE';
                        } else if (filters?.selectedLocationType?.tag === 'COUNTRY') {
                            return endpoint = `menu/metadata/individual/${filters?.selectedLocation?.tag}/3.2?format=client`;
                        } else {
                            break;
                        }
                    default:
                        return;
                }
            }
        }
    }

    getLastPublishedDateForMenu() {
        const filters = this.getFilters();
        const result = this.getLastPublishedEndpoint(filters);
        if (result === 'MOBILE' && filters?.selectedLocationType?.tag === 'LOCATION') {
            this.menuLastPublishedString.next('Download Mobile Menu');
            return;
        }
        if (result && filters.selectedFeatureScope === 'Viewer') {
            return this.api.getLastPublishedDateForMenu(result)?.pipe(take(1)).subscribe({
                next: (response) => {
                    if (filters?.selectedConsumer?.tag === 'MOBILE' && filters?.selectedLocationType?.tag === 'LOCATION') {
                        this.menuLastPublishedString.next('Download Mobile Menu');
                    } else {
                        this.menuLastPublishedString.next(`Last Published ${transformDateService(response?.lastPublishDate)}`);
                    }
                },
                error: (error) => {
                    this.menuLastPublishedString.next(`We couldn't get the last published date.`);
                    if (error.status !== 404) {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                    }
                }
            });
        };
    }

    handleDownloadItemsReportClicked(event) {
        const filters = this.getFilters();
        if (filters?.selectedConsumer?.tag === 'MOBILE') {
            this.downloadItemsReport(event, filters);
        } else {
            this.downloadItemsReport(this.getItemsReportEndpoint(filters), filters);
        }
    }
    downloadItemsReport(endpoint: string, filters) {
        this.api.downloadItemsReport(endpoint)?.pipe(take(1)).subscribe({
            next: (response) => {
                const blob = new Blob([response], {type: 'application/octet-stream'});
                const url = window.URL.createObjectURL(blob);
                const downloadElement = document.createElement('a');
                downloadElement.href = url;
                downloadElement.download = `${filters?.selectedLocation?.tag} - ${filters?.selectedConsumer?.name} Items Report.csv`;
                downloadElement.click();
                window.URL.revokeObjectURL(url);
            },
            error: (error) => {
                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
            }
        });
    }

    getEndpoint(filters) {
        let endpoint = '';
        switch (filters?.selectedConsumer?.tag) {
            case 'COMMERCEPOINT':
                endpoint = `menu/v0.1/commercepoint/${filters?.selectedLocation?.tag}/4.0?format=client`;
                return endpoint;
            case 'COMMERCEPOINT_CATERING':
                endpoint = `menu/v0.1/commercepoint-catering/${filters?.selectedLocation?.tag}/4.0?format=client`;
                return endpoint;
            case 'DCB':
                endpoint = `menu/internal/raw/digital-communications-boards/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'FOOD_TRUCK':
                // individual locations do not have a food truck menu, their venues do
                filters?.selectedLocationType?.tag === 'LOCATION' ?
                    endpoint = `menu/v0.1/${filters?.selectedVenue?.venueId}/4.0?format=client` :
                    endpoint = `menu/v0.1/food-truck/${filters?.selectedLocation?.tag}/3.1?format=client`;
                return endpoint;
            case 'ONLINE_ORDERING_DELIVERY':
                endpoint = `menu/v0.1/catering/delivery/${filters?.selectedLocation?.tag}/3.1?format=client`;
                return endpoint;
            case 'ONLINE_ORDERING_PICKUP':
                endpoint = `menu/v0.1/catering/pickup/${filters?.selectedLocation?.tag}/3.1?format=client`;
                return endpoint;
            case 'SAUCES_DRESSINGS':
                endpoint = `menu/v0.1/sauces/${filters?.selectedLocation?.tag}/3.1?format=client`;
                return endpoint;
            case 'SPOTLIGHT':
                endpoint = `menu/v0.1/spotlight/${filters?.selectedLocation?.tag}/3.2?format=client`;
                return endpoint;
            case 'MASTER_REWARDS_MENU':
                if (filters?.selectedLocationType?.tag === 'COUNTRY') {
                    return endpoint = `menu/v0.1/master-rewards/${filters?.selectedLocation?.tag}/3.2?format=client`;
                } else {
                    break;
                }
            case 'MEMBERSHIP_REWARDS':
                return endpoint = `menu/v0.1/rewards/${filters?.selectedLocation?.tag}/3.2?format=client`;
            case 'MOBILE':
                if (filters?.selectedLocationType?.tag === 'COUNTRY') {
                    endpoint = `menu/v0.1/individual/${filters?.selectedLocation?.tag}/3.1?format=client`;
                    return endpoint;
                } else {
                    break;
                }
            default:
                this.notificationService.error(`${filters?.selectedConsumer?.tag} not found. Please try a different consumer.`);
                break;
        }
    }

    getItemsReportEndpoint(filters) {
        let endpoint = '';
        switch (filters?.selectedConsumer?.tag) {
            case 'COMMERCEPOINT':
                endpoint = `reports/items/commercepoint/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'COMMERCEPOINT_CATERING':
                endpoint = `reports/items/commercepoint-catering/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'DCB':
                endpoint = `reports/items/digital-communications-boards/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'FOOD_TRUCK':
                if (filters?.selectedLocationType?.tag === 'CHAIN') {
                    endpoint = `reports/items/food-truck/CHAIN`;
                    return endpoint;
                } else if (filters?.selectedLocationType?.tag === 'LOCATION' && filters?.selectedVenue) {
                    endpoint = `reports/items/${filters?.selectedVenue.venueId}`;
                    return endpoint;
                } else {
                    this.notificationService.error('Items Report can only be downloaded for CHAIN or individual locations.')
                    break;
                }
            case 'MEMBERSHIP_REWARDS':
                endpoint = `reports/items/membership-rewards/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'ONLINE_ORDERING_DELIVERY':
                endpoint = `reports/items/online-ordering-delivery/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'ONLINE_ORDERING_PICKUP':
                endpoint = `reports/items/online-ordering-pickup/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'SAUCES_DRESSINGS':
                endpoint = `reports/items/sauces-dressings/${filters?.selectedLocation?.tag}`;
                return endpoint;
            case 'SPOTLIGHT':
                endpoint = `reports/items/spotlight/${filters?.selectedLocation?.tag}`;
                return endpoint;
            default:
                this.notificationService.error(`${filters?.selectedConsumer?.tag} not found. Please try a different consumer.`);
                break;
        }
    }

    drop(event: CdkDragDrop<any>) {
        moveItemInArray(this.sortedPreviewNodes, event.previousIndex, event.currentIndex);

        // Change sortOrder value on Submenu to reflect new location in sort
        const sortNodeIndex = this.sortedPreviewNodes.findIndex(nd => +nd.id === event.currentIndex);
        const sortNode = Object.assign({}, this.sortedPreviewNodes[sortNodeIndex]);

        sortNode.sortOrder = event.currentIndex;
        this.sortedPreviewNodes[sortNodeIndex] = sortNode;

        this.resetSortOrderValues();
    }

    orderNodesBySortOrder() {
        this.sortedPreviewNodes.sort((a, b) => (a.sortOrder > b.sortOrder) ? 1 : -1);
    }

    resetSortOrderValues() {
        this.sortedPreviewNodes.forEach((spn, index) => {
            const sortNode = Object.assign({}, spn);
            sortNode.sortOrder = index;
            this.sortedPreviewNodes[index] = sortNode;
        });
        return this.sortedPreviewNodes;
    }

    getSubmenuCacheEnabled() {
        this.treeFacadeBasic.getSubmenuCacheEnabled()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(response => {
            this.isSubmenuCacheEnabled = response;
        });
    }

    clearSubmenuCache() {
        this.treeFacadeBasic.clearSubmenuCache()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(response => {
            this.notificationService.success('Submenu cache cleared. Please reload submenu.');
        });
    }


    changeFilterType(event) {
        if (event) {
            this.treeFacade.loadSubmenus();
        }
    }

    // tslint:disable-next-line:no-shadowed-variable
    onFilter(filter: string): void {
        this.searchedTag$.next(filter);
        this.isSearchMode$.next(true);
        this.isSearching = of(true);
        const filters = this.getFilters();
        this.api.getMenuFilteredByItemTag(filters?.selectedFeatureScope, filter, filters?.selectedConsumer?.tag,
            filters?.selectedEffectiveDate)
            .pipe(
                map((submenuNodes: any[]) => RA.isNonEmptyArray(submenuNodes) ?
                    submenuNodes.map(this.mapFilteredMenuItemToNode) : [])
            )
            .subscribe(
                {
                    next: (result) => {
                        this.searchFailed.next(false);
                        this.isAutoExpanding.next(true);
                        if (result.length > 0) {
                            setTimeout(() => {
                                this.showSearchSnackbar();
                            }, 1);
                            this.filteredMenuTree$ = of(result);
                            setTimeout(() => {
                                this.expandAllSearchNodes().then();
                            }, 100);
                            this.isSearching = of(false);
                            setTimeout(() => {
                                this.isAutoExpanding.next(false);
                            }, 1000);
                        } else {
                            this.filteredMenuTree$ = of(result);
                            this.searchFailed.next(true);
                            setTimeout(() => {
                                this.showSearchFailedSnackbar();
                            }, 1);
                            this.isAutoExpanding.next(false);
                            this.isSearching = of(false);
                        }
                    },
                    error: (error) => {
                        this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        this.isSearching = of(false);
                    },
                }
            );
    }

    async expandAllSearchNodes() {
        await this.searchTree?.menuTree.treeModel.expandAll();
    };

    showSearchSnackbar() {
        const searchSnackbar = document.getElementById('searchSnackbar');
        searchSnackbar.className = 'show';
        setTimeout(() => {
            searchSnackbar.className = searchSnackbar.className.replace('show', '');
        }, 5000);
    }

    showSearchFailedSnackbar() {
        const searchFailedSnackbar = document.getElementById('searchFailed');
        searchFailedSnackbar.className = 'show';
        setTimeout(() => {
            searchFailedSnackbar.className = searchFailedSnackbar.className.replace('show', '');
        }, 2500);
    }

    showMoveFailedSnackbar() {
        const moveFailedSnackbar = document.getElementById('moveFailed');
        moveFailedSnackbar.className = 'show';
        setTimeout(() => {
            moveFailedSnackbar.className = moveFailedSnackbar.className.replace('show', '');
        }, 7500);
    }

    filterNodes(value: string, selected: string): void {
        if (value === '') {
            this.treeFacade.loadSubmenus();
        } else {
            this.nodes$.pipe(
                take(1),
                map(list => {
                    const filteredNodes = list.filter(nd => nd[selected].toLowerCase().includes(value.toLowerCase()));
                    this.treeFacade.filterSubmenus(filteredNodes);
                }),
                takeUntilDestroyed(this.destroyRef)
            ).subscribe();
        }
    }

    exitSearchMode() {
        this.isSearchMode$.next(false);
        this.store.dispatch(SubmenusActions.deselectSearchSubmenu());
    }

    mapFilteredMenuItemToNode = (item) => {
        const node: any = {
            data: item,
            hasChildren: item.hasChildren ? item.hasChildren : item?.items?.length > 0,
            id: item.tagPath,
            name: item.name,
        };

        if (node?.hasChildren && item?.items?.length > 0) {
            node.children = item?.items?.map(value => this.mapFilteredMenuItemToNode(value));
        }
        return node;
    }

    copyTagpath(node: TreeNode): string {
        const item = node.data.data;
        if (item.tagPath) {
            this.clipboard.copy(item.tagPath);
            this.notificationService.success(`Copied: ${item.tagPath}`);
        } else {
            this.notificationService.error('This item does not have a tagpath');
        }
        return item.tagPath;
    }
}
